import { CamundaBaseService } from './base-service';
import type {
  CamundaFormObject,
  CamundaFormValue,
  CamundaFormValues,
  CamundaJsonObject,
  CamundaJsonValue,
  CamundaValueInfo,
  StartProcessRequest,
  TaskFormVariable,
  TaskFormVariables,
  StartProcessResult,
} from './types';
import { BaseFormSchema } from '@/types/zod-schema';
import { resolveFormKey } from '@/lib/process-registry';
import { FORM_SCHEMA_LOADERS } from '@/lib/form-registry.generated';
import { encodeCamundaPathSegment } from './process-definition-id';
import type { HistoricVariableInstance } from './history-types';
import { normalizeGestionClientesForRpa } from '@/forms/comercial/gestion-clientes/gestion-clientes/rpa-variables';
import { normalizeAltaContactoForRpa } from '@/forms/comercial/creacion-contactos/alta-contactos/rpa-variables';
import { prepareAnalisisLaboralSubmission } from '@/forms/laboral/process/analisis-laboral/submission';
import { normalizeClosedWonForRpa, recoverClosedWonFormValues } from '@/forms/comercial/alta-propuesta/closed-won-client-data';
import {
  isSerializedMultiFileArray,
  reconstituteMultiFileItems,
  type SerializedMultiFileItem,
} from '@/lib/camunda/form-file-values';
import {
  canonicalSpanishIbanFromLegacyCcc,
  normalizeHubSpotIban,
} from '@/lib/hubspot/iban';
import { normalizeRiskCompanyIdentity } from '@/forms/comercial/aprobacion-riesgos/shared/risk-form-hydration';
import { z } from 'zod';

export interface TaskFormInfo {
  key: string;
  contextPath?: string;
  embedded?: boolean;
}

export interface StartFormInfo {
  key: string;
  contextPath?: string;
  embedded?: boolean;
}

export interface StartFormVariables {
  [key: string]: TaskFormVariable;
}

const isObjectSchemaLike = <TValue>(value: TValue): value is TValue & BaseFormSchema =>
  value instanceof z.ZodObject;

const FORM_SCHEMA_FIELDS_KEY = 'shape' as const;

interface HistoricFileMetadata {
  filename: string | null;
  mimeType: string | null;
}

type SchemaFormKey = keyof typeof FORM_SCHEMA_LOADERS;

const isSchemaFormKey = (formKey: string): formKey is SchemaFormKey =>
  Object.hasOwn(FORM_SCHEMA_LOADERS, formKey);

const getSchemaLoader = (formKey: string) => {
  if (!isSchemaFormKey(formKey)) {
    return undefined;
  }
  return FORM_SCHEMA_LOADERS[formKey];
};

const DEBUG_CAMUNDA_FORMS = import.meta.env.VITE_DEBUG_CAMUNDA_FORMS === 'true';
const LEGACY_RPA_RECOVERY_CCC_VARIABLE = 'ccc';
const RISK_PROCESS_FORM_KEYS = new Set([
  'adjuntar-alternativa-riesgos',
  'alta-riesgos',
  'aprobacion-alta-net4',
  'cambios-riesgo',
  'cualificacion-riesgos',
  'cuenta-contable-riesgos',
  'evaluacion-riesgos',
  'revision-riesgos',
]);

const camundaStringValue = z.string().catch('');
const stringSchema = z.string();
const booleanSchema = z.boolean();
const camundaScalarValue = z.union([z.string(), z.number(), z.boolean(), z.null(), z.undefined()]);
const hubSpotScalarValue = z.union([z.string(), z.number(), z.null(), z.undefined()]);
type CamundaScalarValue = z.input<typeof camundaScalarValue>;

const camundaJsonValueSchema: z.ZodType<CamundaJsonValue> = z.lazy(() => z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(camundaJsonValueSchema),
  z.record(z.string(), camundaJsonValueSchema),
]));

const camundaFormValueSchema: z.ZodType<CamundaFormValue> = z.lazy(() => z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.undefined(),
  z.instanceof(File),
  z.instanceof(Blob),
  z.array(camundaFormValueSchema),
  z.record(z.string(), camundaFormValueSchema),
]));

const camundaFormFileItemSchema = z.object({
  file: z.instanceof(File),
  tipo: z.string(),
  filename: z.string().optional(),
  id: z.string().optional(),
  documentId: z.string().optional(),
  documentTitle: z.string().optional(),
  fileVariableName: z.string().optional(),
  notes: z.string().optional(),
  persisted: z.boolean().optional(),
  revision: z.number().int().positive().optional(),
  source: z.string().optional(),
}).catchall(camundaJsonValueSchema);

const MULTI_FILE_KNOWN_KEYS = new Set([
  'file',
  'tipo',
  'filename',
  'id',
  'documentId',
  'documentTitle',
  'fileVariableName',
  'notes',
  'persisted',
  'revision',
  'source',
]);

const TASK_FORM_BINARY_CONCURRENCY = 4;

interface CamundaMultiFileItem extends CamundaFormObject {
  file: File;
  tipo: string;
  filename?: string;
  id?: string;
  documentId?: string;
  documentTitle?: string;
  fileVariableName?: string;
  notes?: string;
  persisted?: boolean;
  revision?: number;
  source?: string;
  metadata?: CamundaJsonObject;
}

const normalizeCamundaMultiFileItem = (value: CamundaFormValue): CamundaMultiFileItem => {
  const parsed = camundaFormFileItemSchema.safeParse(value);
  if (!parsed.success) {
    throw new Error('El elemento MultiFile no cumple el contrato de Camunda.');
  }

  const metadata: CamundaJsonObject = {};
  for (const [key, nestedValue] of Object.entries(parsed.data)) {
    if (MULTI_FILE_KNOWN_KEYS.has(key)) {
      continue;
    }
    const parsedMetadata = camundaJsonValueSchema.safeParse(nestedValue);
    if (parsedMetadata.success) {
      metadata[key] = parsedMetadata.data;
    }
  }

  return {
    file: parsed.data.file,
    tipo: parsed.data.tipo,
    filename: parsed.data.filename,
    id: parsed.data.id,
    documentId: parsed.data.documentId,
    documentTitle: parsed.data.documentTitle,
    fileVariableName: parsed.data.fileVariableName,
    notes: parsed.data.notes,
    persisted: parsed.data.persisted,
    revision: parsed.data.revision,
    source: parsed.data.source,
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  };
};

const parseCamundaFormValues = <TValues extends object>(values: TValues): CamundaFormValues => {
  const parsedValues: CamundaFormValues = {};
  for (const [key, value] of Object.entries(values)) {
    const parsedValue = camundaFormValueSchema.safeParse(value);
    if (!parsedValue.success) {
      throw new Error(`La variable ${key} no tiene un valor serializable para Camunda.`);
    }
    parsedValues[key] = parsedValue.data;
  }
  return parsedValues;
};

const documentVersionForCamundaSchema = z.object({
  file: z.instanceof(File).nullish(),
  documentId: z.string().optional(),
  documentTitle: z.string().optional(),
  tipo: z.string().optional(),
  notes: z.string().optional(),
  fileVariableName: z.string().optional(),
  filename: z.string().optional(),
  persisted: z.boolean().optional(),
  revision: z.number().int().positive().optional(),
});

type RawDocumentVersionForCamunda = z.infer<
  typeof documentVersionForCamundaSchema
>;
type DocumentVersionForCamunda = Omit<
  RawDocumentVersionForCamunda,
  'documentId'
> & { documentId: string };

const VERSIONED_DOCUMENT_FIELDS = new Map([
  ['signature_document_versions', 'signature_document_file_'],
  ['documentos_valoracion', 'valoracion_document_file_'],
]);

const LEGACY_SIGNATURE_DOCUMENT_VARIABLE = /^signature_document_file_(.+)_revision_\d+$/;
const documentVersionsSchema = documentVersionForCamundaSchema.array();

const getLegacySignatureDocumentId = (
  item: RawDocumentVersionForCamunda,
  index: number,
): string => {
  const documentId = item.documentId?.trim();
  if (documentId) return documentId;

  const variableMatch = item.fileVariableName?.match(LEGACY_SIGNATURE_DOCUMENT_VARIABLE);
  if (variableMatch?.[1]) return variableMatch[1];

  const filename = item.filename ?? item.file?.name;
  if (!filename?.trim()) {
    throw new Error(
      `La revisión de firma ${index + 1} no tiene documentId ni nombre de archivo legacy.`,
    );
  }

  const filenameStem = filename
    .replace(/\.[^/.]+$/, '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
  if (!filenameStem) {
    throw new Error(`La revisión de firma ${index + 1} tiene un nombre de archivo inválido.`);
  }

  return `legacy_${filenameStem}_${index + 1}`;
};

const parseDocumentVersionsForCamunda = (
  value: Parameters<typeof documentVersionsSchema.safeParse>[0],
  name = 'signature_document_versions',
): DocumentVersionForCamunda[] => {
  const result = documentVersionsSchema.safeParse(value);
  if (!result.success) {
    throw new Error('Las revisiones documentales tienen un formato no válido.');
  }

  return result.data.map((item, index) => ({
    ...item,
    documentId: name === 'signature_document_versions' ? getLegacySignatureDocumentId(item, index) : z.string().trim().min(1).parse(item.documentId),
  }));
};

const migrateLegacyRpaRecoveryAccount = (
  variables: CamundaFormValues,
): CamundaFormValues => {
  const {
    [LEGACY_RPA_RECOVERY_CCC_VARIABLE]: legacyCcc,
    ...currentVariables
  } = variables;
  const currentIban = camundaScalarValue.safeParse(currentVariables.iban);
  const currentIbanValue = currentIban.success
    ? hubSpotScalarValue.safeParse(currentIban.data)
    : { success: false as const };
  if (currentIbanValue.success && normalizeHubSpotIban(currentIbanValue.data)) {
    return currentVariables;
  }

  const legacyCccValue = camundaScalarValue.safeParse(legacyCcc);
  const legacyCccInput = legacyCccValue.success
    ? hubSpotScalarValue.safeParse(legacyCccValue.data)
    : { success: false as const };
  const migratedIban = legacyCccInput.success
    ? canonicalSpanishIbanFromLegacyCcc(legacyCccInput.data)
    : '';
  if (!migratedIban) {
    return currentVariables;
  }
  return { ...currentVariables, iban: migratedIban };
};

const getFormKeyName = (formKey: string): string =>
  formKey.replaceAll('\\', '/').split('/').filter(Boolean).at(-1) ?? formKey;

const omitClosedWonSnapshot = (formKey: string, values: CamundaFormValues): CamundaFormValues => {
  if (getFormKeyName(formKey) !== 'alta-propuesta') return values;
  return Object.fromEntries(Object.entries(values).filter(([name]) =>
    name !== 'closed_won_client_data' && name !== 'closed_won_recovery_data'));
};

const migrateLegacyRiskCompanyVariables = (
  formKey: string,
  variables: CamundaFormValues,
): CamundaFormValues => {
  if (!RISK_PROCESS_FORM_KEYS.has(getFormKeyName(formKey))) {
    return variables;
  }

  const { companyId, companyLabel } = normalizeRiskCompanyIdentity(variables);
  const migratedVariables = { ...variables };
  if (companyId) {
    migratedVariables.company_id = companyId;
  }
  if (companyLabel) {
    migratedVariables.company_id_label = companyLabel;
  }
  return migratedVariables;
};

type LogSummaryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | LogSummaryValue[]
  | { [key: string]: LogSummaryValue };

const summarizeForLog = <TValue>(value: TValue, depth = 0): LogSummaryValue => {
  if (globalThis.File && value instanceof globalThis.File) {
    return { kind: 'File', name: value.name, size: value.size, type: value.type };
  }
  if (globalThis.Blob && value instanceof globalThis.Blob) {
    return { kind: 'Blob', size: value.size, type: value.type };
  }
  const primitive = camundaScalarValue.safeParse(value);
  if (primitive.success) {
    const primitiveValue = primitive.data;
    const primitiveString = stringSchema.safeParse(primitiveValue);
    if (primitiveString.success) {
      return primitiveString.data.length > 300
        ? `[string ${primitiveString.data.length} chars]`
        : primitiveString.data;
    }
    return primitiveValue;
  }
  if (Array.isArray(value)) {
    if (depth >= 3) return `[array ${value.length} items]`;
    return value.slice(0, 10).map((item) => summarizeForLog(item, depth + 1));
  }
  if (value instanceof Object) {
    if (depth >= 3) return '[object]';
    return Object.fromEntries(
      Object.entries(value)
        .slice(0, 30)
        .map(([key, nestedValue]) => [key, summarizeForLog(nestedValue, depth + 1)]),
    );
  }
  return null;
};

const debugCamundaForms = (...args: unknown[]) => {
  if (DEBUG_CAMUNDA_FORMS) {
    console.log(...args);
  }
};

const accountReviewSelectionFields = ['gestion_cuentas_net4_revision_accion', 'gestion_cuentas_net4_revision', 'gestion_cuentas_net4_revision_indice'] as const;

const normalizeValuesForCamunda = (
  values: CamundaFormValues,
  formKey?: string,
): CamundaFormValues => {
  if (formKey === 'revision-gestion-cuentas') {
    return Object.fromEntries(accountReviewSelectionFields.map(name => [name, values[name]]));
  }
  if (formKey === 'alta-propuesta' || formKey === 'revision-rpa-cliente') {
    const inputValues = formKey === 'revision-rpa-cliente'
      ? Object.fromEntries(Object.entries(values).filter(([name]) => name !== 'error'))
      : values;
    return normalizeClosedWonForRpa(inputValues);
  }
  if (formKey === 'gestion-clientes') {
    return parseCamundaFormValues(normalizeGestionClientesForRpa({
      ...values,
      gestion_cliente_accion: camundaStringValue.parse(values.gestion_cliente_accion),
      base_datos_m4: camundaStringValue.parse(values.base_datos_m4),
    }));
  }

  if (formKey === 'alta-contactos') {
    const scalarValues: Record<string, CamundaScalarValue> = {};
    for (const [key, value] of Object.entries(values)) {
      const parsedValue = camundaScalarValue.safeParse(value);
      if (parsedValue.success) {
        scalarValues[key] = parsedValue.data;
      }
    }
    return parseCamundaFormValues({
      ...values,
      ...normalizeAltaContactoForRpa(scalarValues),
    });
  }

  return values;
};


export class CamundaFormsService extends CamundaBaseService {
  static resolveHistoricFileMetadata(
    variable: Pick<HistoricVariableInstance, 'value'> & { valueInfo?: CamundaValueInfo },
  ): HistoricFileMetadata {
    const historicFileMetadataSchema = z.object({
      filename: z.string().optional(),
      mimeType: z.string().optional(),
    });
    const valueInfo = historicFileMetadataSchema.safeParse(variable.valueInfo);
    const valueAsObject = historicFileMetadataSchema.safeParse(variable.value);
    const parsedValueInfo = valueInfo.success ? valueInfo.data : undefined;
    const parsedValueAsObject = valueAsObject.success ? valueAsObject.data : undefined;
    const filename = parsedValueInfo?.filename ?? parsedValueAsObject?.filename ?? null;
    const mimeType = parsedValueInfo?.mimeType ?? parsedValueAsObject?.mimeType ?? null;
    return { filename, mimeType };
  }

  static async parseHistoricVariableValue(
    variable: Pick<HistoricVariableInstance, 'id' | 'type' | 'value'> & {
      valueInfo?: CamundaValueInfo;
    },
    loadFileBinary?: () => Promise<Blob | null>,
  ): Promise<CamundaFormValue> {
    if (variable.type === 'File') {
      const { filename, mimeType: storedMimeType } = this.resolveHistoricFileMetadata(variable);
      if (!filename) {
        return null;
      }

      if (loadFileBinary) {
        const fileContent = await loadFileBinary();
        if (fileContent) {
          const mimeType =
            storedMimeType ||
            this.getMimeTypeFromFilename(filename) ||
            fileContent.type ||
            'application/octet-stream';
          return new File([fileContent], filename, { type: mimeType });
        }
      }

      return null;
    }

    if (variable.type === 'Object') {
      try {
        const rawValue = variable.value;
        const stringValue = stringSchema.safeParse(rawValue);
        const parsed = stringValue.success ? JSON.parse(stringValue.data) : rawValue;

        if (isSerializedMultiFileArray(parsed)) {
          return reconstituteMultiFileItems(parsed);
        }

        const parsedValue = camundaFormValueSchema.safeParse(parsed);
        return parsedValue.success ? parsedValue.data : null;
      } catch (error) {
        console.warn('Error parsing historic Object variable:', error);
        return null;
      }
    }

    const parsedValue = camundaFormValueSchema.safeParse(variable.value);
    return parsedValue.success ? parsedValue.data : null;
  }

  static async getTaskForm(taskId: string): Promise<TaskFormInfo | null> {
    try {
      debugCamundaForms('Obteniendo informacion del formulario para tarea:', taskId);
      const formInfo = await this.makeRequest<TaskFormInfo>(`/task/${taskId}/form`);
      return formInfo;
    } catch (error) {
      if (DEBUG_CAMUNDA_FORMS) {
        console.error('Error fetching task form info:', error);
      }
      throw error;
    }
  }

  static async completeTaskWithFormData(
    taskId: string,
    values: CamundaFormValues,
    formKey?: string,
  ): Promise<boolean> {
    try {
      debugCamundaForms('Iniciando completeTaskWithFormData', { taskId });
      debugCamundaForms('Valores originales:', summarizeForLog(values));
      const documentCollections = [...VERSIONED_DOCUMENT_FIELDS].flatMap(([name, prefix]) =>
        Array.isArray(values[name]) ? [{ name, prefix, versions: parseDocumentVersionsForCamunda(values[name], name) }] : [],
      );

      const submissionValues = formKey === 'analisis-laboral'
        ? prepareAnalisisLaboralSubmission(values)
        : values;
      const variables = await this.prepareVariablesForCamunda(submissionValues, formKey, { taskId, publishDocuments: true });
      debugCamundaForms('Variables preparadas para Camunda:', summarizeForLog(variables));

      const payload = { variables };
      const payloadJson = JSON.stringify(payload);
      debugCamundaForms('Payload final:', summarizeForLog(payload));

      await this.makeRequest(`/task/${taskId}/complete`, {
        method: 'POST',
        body: payloadJson,
        suppressErrorLogStatuses: [400, 409, 500],
      });
      for (const { name, prefix, versions } of documentCollections) {
        values[name] = this.markDocumentFilesPersisted(versions, prefix);
      }

      debugCamundaForms('Task completada exitosamente');
      return true;
    } catch (error) {
      if (DEBUG_CAMUNDA_FORMS) {
        console.error('Error completing task:', error);
      }
      throw error;
    }
  }

  static async saveTaskFormVariables(
    taskId: string,
    values: CamundaFormValues,
    formKey?: string,
  ): Promise<boolean> {
    await this.persistTaskFormDraft(taskId, values, formKey);
    return true;
  }

  static async persistTaskFormDraft(
    taskId: string,
    values: CamundaFormValues,
    formKey?: string,
  ): Promise<void> {
    try {
      const documentCollections = [...VERSIONED_DOCUMENT_FIELDS].flatMap(([name, prefix]) =>
        Array.isArray(values[name]) ? [{ name, prefix, versions: parseDocumentVersionsForCamunda(values[name], name) }] : [],
      );
      const modifications = await this.prepareVariablesForCamunda(values, formKey);
      if (Object.keys(modifications).length > 0) {
        const payload = { modifications };
        await this.makeRequest(`/task/${taskId}/variables`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        for (const { name, prefix, versions } of documentCollections) {
          values[name] = this.markDocumentFilesPersisted(versions, prefix);
        }
      }
    } catch (error) {
      console.error('Error persisting task form draft:', error);
      throw error;
    }
  }

  private static async prepareVariablesForCamunda(
    values: CamundaFormValues,
    formKey?: string,
    options: { taskId?: string; publishDocuments?: boolean } = {},
  ): Promise<TaskFormVariables> {
    console.log('🔧 Preparando variables para Camunda...');

    const normalizedValues = normalizeValuesForCamunda(values, formKey);
    let schema = null;
    
    if (formKey) {
      try {
        schema = await this.loadFormSchema(formKey);
      } catch (error) {
        console.warn(`⚠️ No se pudo cargar el schema para ${formKey}:`, error);
      }
    }

    const camundaVariables: TaskFormVariables = {};
    
    const variablesToProcess = formKey === 'revision-gestion-cuentas'
      ? accountReviewSelectionFields
      : schema
      ? Object.keys(schema[FORM_SCHEMA_FIELDS_KEY])
      : Object.keys(normalizedValues);
    
    for (const key of variablesToProcess) {
      let value = normalizedValues[key];
      
      if (value === undefined && schema) {
        const fieldSchema = schema[FORM_SCHEMA_FIELDS_KEY][key];
        const missingBooleanValue = fieldSchema
          ? this.resolveMissingBooleanValue(fieldSchema)
          : undefined;
        if (missingBooleanValue !== undefined) {
          value = missingBooleanValue;
          console.log(`🔄 Aplicando ${String(missingBooleanValue)} a ${key} (campo boolean)`);
        }
      }
      
      if (value !== undefined) {
        if (formKey === 'revision-gestion-cuentas' && key === 'gestion_cuentas_net4_revision_indice') {
          camundaVariables[key] = { value, type: 'Integer' };
        } else if (VERSIONED_DOCUMENT_FIELDS.has(key) && Array.isArray(value)) {
          const documentVersions = parseDocumentVersionsForCamunda(value, key);
          const fileVariablePrefix = VERSIONED_DOCUMENT_FIELDS.get(key);
          const preparedDocumentItems = documentVersions.map((item, revisionIndex) => {
            const revision = item.revision ?? revisionIndex + 1;
            const baseVariableName = `${fileVariablePrefix}${item.documentId}`;
            const persistedFileVariableName = item.fileVariableName?.trim() ?? '';
            const fileVariableName = item.persisted === true && persistedFileVariableName
              ? persistedFileVariableName
              : `${baseVariableName}_revision_${revision}`;
            return { item, revision, fileVariableName };
          });
          const fileVariableNames = new Set<string>();
          for (const { fileVariableName } of preparedDocumentItems) {
            if (fileVariableNames.has(fileVariableName)) {
              throw new Error(`La revisión documental repite la variable ${fileVariableName}.`);
            }
            fileVariableNames.add(fileVariableName);
          }
          const serializedItems: SerializedMultiFileItem[] = await Promise.all(
            preparedDocumentItems.map(async ({ item, revision, fileVariableName }) => {
              const { file, ...metadata } = item;
              if (file && item.persisted !== true) {
                const fileContent = await this.fileToBase64(file);
                camundaVariables[fileVariableName] = {
                  value: fileContent,
                  type: 'File',
                  valueInfo: { filename: file.name, mimeType: file.type },
                };
              }
              return {
                ...metadata,
                tipo: item.tipo ?? '',
                filename: file?.name ?? item.filename ?? '',
                fileVariableName,
                persisted: true,
                revision,
              } satisfies SerializedMultiFileItem;
            }),
          );
          camundaVariables[key] = {
            type: 'Object',
            valueInfo: {
              objectTypeName: 'java.util.ArrayList',
              serializationDataFormat: 'application/json',
            },
            value: JSON.stringify(serializedItems),
          };
          if (key === 'documentos_valoracion' && options.publishDocuments) {
            const latest = new Map<string, (typeof preparedDocumentItems)[number]>();
            for (const prepared of preparedDocumentItems) {
              const previous = latest.get(prepared.item.documentId);
              if (!previous || prepared.revision > previous.revision) latest.set(prepared.item.documentId, prepared);
            }
            const documents = await Promise.all([...latest.values()].map(async ({ item, fileVariableName }) => {
              const file = item.file ?? (options.taskId && item.persisted === true
                ? await this.getTaskVariableBinary(options.taskId, fileVariableName)
                : null);
              if (!file) throw new Error(`No está disponible la revisión de ${item.filename ?? item.documentId}.`);
              return { tipo: item.tipo, filename: item.file?.name ?? item.filename, mimeType: item.file?.type ?? this.getMimeTypeFromFilename(item.filename ?? '') ?? file.type, content: await this.fileToBase64(file) };
            }));
            camundaVariables.documentos_valoracion_publicacion = {
              type: 'Object',
              valueInfo: { objectTypeName: 'java.util.ArrayList', serializationDataFormat: 'application/json' },
              value: JSON.stringify(documents),
            };
          }
        } else if (VERSIONED_DOCUMENT_FIELDS.has(key) && value === null) {
          camundaVariables[key] = { value: null, type: 'Null' };
        } else if (value instanceof File) {
          const base64Value = await this.fileToBase64(value);
          camundaVariables[key] = {
            value: base64Value,
            type: 'File',
            valueInfo: { filename: value.name }
          };
        } else if (this.isMultiFileArray(value)) {
          const serializedItems = await Promise.all(
            value.map(async (rawItem) => {
              const item = normalizeCamundaMultiFileItem(rawItem);
              const { file, metadata: itemMetadata, ...knownMetadata } = item;
              const serializedMetadata: CamundaJsonObject = {
                ...itemMetadata,
                size: file.size,
              };
              const serializedItem: SerializedMultiFileItem = {
                ...knownMetadata,
                filename: file.name,
                mimeType: file.type,
                content: await this.fileToBase64(file),
                metadata: serializedMetadata,
              };
              return serializedItem;
            })
          );
          camundaVariables[key] = {
            type: "Object",
            valueInfo: {
              objectTypeName: "java.util.ArrayList",
              serializationDataFormat: "application/json"
            },
            value: JSON.stringify(serializedItems)
          };
        } else if (Array.isArray(value)) {
          camundaVariables[key] = {
            type: "Object",
            valueInfo: {
              objectTypeName: "java.util.ArrayList",
              serializationDataFormat: "application/json"
            },
            value: JSON.stringify(value)
          };
        } else if (value instanceof Object) {
          camundaVariables[key] = { 
            value: JSON.stringify(value), 
            type: 'Json' 
          };
        } else if (value === null) {
          camundaVariables[key] = { value: null, type: 'Null' };
        } else {
          camundaVariables[key] = { value };
        }
      }
    }
    debugCamundaForms('Variables preparadas:', summarizeForLog(camundaVariables));
    return camundaVariables;
  }

  private static resolveMissingBooleanValue(fieldSchema: z.ZodType): boolean | undefined {
    const parsedDefault = fieldSchema.safeParse(undefined);
    if (parsedDefault.success) {
      const parsedBooleanDefault = booleanSchema.safeParse(parsedDefault.data);
      if (parsedBooleanDefault.success) {
        return parsedBooleanDefault.data;
      }
    }

    return undefined;
  }

  private static isMultiFileArray<TValue>(
    value: TValue,
  ): value is TValue & CamundaMultiFileItem[] {
    return Array.isArray(value)
      && value.length > 0
      && value.every((item) => camundaFormFileItemSchema.safeParse(item).success);
  }

  private static markDocumentFilesPersisted(
    versions: DocumentVersionForCamunda[],
    prefix: string,
  ): DocumentVersionForCamunda[] {
    for (const [revisionIndex, item] of versions.entries()) {
      if (!item.file || item.persisted === true) continue;
      const revision = item.revision ?? revisionIndex + 1;
      const documentId = item.documentId;
      item.revision = revision;
      item.fileVariableName = `${prefix}${documentId}_revision_${revision}`;
      item.filename = item.file.name;
      item.persisted = true;
      delete item.file;
    }
    return versions;
  }

  private static isSerializedMultiFileArray<TValue>(
    parsed: TValue,
  ): parsed is TValue & SerializedMultiFileItem[] {
    return isSerializedMultiFileArray(parsed);
  }

  private static reconstituteMultiFileItems(
    serializedItems: Parameters<typeof reconstituteMultiFileItems>[0],
  ): ReturnType<typeof reconstituteMultiFileItems> {
    return reconstituteMultiFileItems(serializedItems);
  }

  private static fileToBase64(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = stringSchema.safeParse(reader.result);
        if (!result.success) {
          reject(new Error('El navegador no devolvió el archivo como un Data URL de texto.'));
          return;
        }
        const base64 = result.data.split(',')[1];
        resolve(base64 ?? '');
      };
      reader.onerror = error => reject(error);
    });
  }

  static async getTaskVariableBinary(taskId: string, variableName: string): Promise<Blob | null> {
    try {
      console.log(`Descargando binario para la variable ${variableName} en la tarea ${taskId}`);
      const response = await this.makeRequest<Response>(`/task/${taskId}/variables/${variableName}/data`, {
        rawResponse: true
      });
      if (response.ok) {
        const blob = await response.blob();
        return blob;
      }
      console.error(`Error al descargar el fichero: ${response.statusText}`);
      return null;
    } catch (error) {
      console.error('Error fetching task variable binary:', error);
      return null;
    }
  }

  static async getStartForm(processDefinitionId: string): Promise<StartFormInfo | null> {
    try {
      console.log('Obteniendo información del start form para proceso:', processDefinitionId);
      const encodedProcessDefinitionId = encodeCamundaPathSegment(processDefinitionId);
      const formInfo = await this.makeRequest<StartFormInfo>(`/process-definition/${encodedProcessDefinitionId}/startForm`);
      return formInfo;
    } catch (error) {
      console.error('Error fetching start form info:', error);
      return null;
    }
  }

  static async submitStartForm(
    processDefinitionId: string,
    variables: CamundaFormValues,
    businessKey?: string,
    formKey?: string,
  ): Promise<StartProcessResult | null> {
    try {
      const camundaVariables = await this.prepareVariablesForCamunda(
        variables,
        formKey,
      );

      const requestBody: StartProcessRequest = {
        variables: camundaVariables,
      };
  
      if (businessKey) {
        requestBody.businessKey = businessKey;
      }
  
      const encodedProcessDefinitionId = encodeCamundaPathSegment(processDefinitionId);
      const result = await this.makeRequest<StartProcessResult>(
        `/process-definition/${encodedProcessDefinitionId}/submit-form`,
        {
          method: 'POST',
          body: JSON.stringify(requestBody),
        },
      );

      return result;
    } catch (error) {
      console.error('Error submitting start form:', error);
      return null;
    }
  }


  static extractVariableNamesFromSchema(schema: BaseFormSchema): string[] {
    try {
      const schemaFields = schema[FORM_SCHEMA_FIELDS_KEY];
      return Object.keys(schemaFields);
    } catch (error) {
      console.warn('Error extracting variable names from schema:', error);
      return [];
    }
  }

  static async loadFormSchema(formKey: string, availableForms?: string[]): Promise<BaseFormSchema | null> {
    try {
      const resolvedFormKey = availableForms ? resolveFormKey(formKey, availableForms) : formKey;
      if (!resolvedFormKey) {
        console.error(`❌ No matching form found for "${formKey}" while loading schema.`);
        return null;
      }

      const loader = getSchemaLoader(resolvedFormKey);
      if (!loader) {
        console.error(`❌ No schema loader found for "${resolvedFormKey}".`);
        return null;
      }

      const schemaModule = await loader();
      const schemaName = `${resolvedFormKey.replace(/-/g, '')}Schema`;
      const moduleExports = Object.entries(schemaModule);
      const loadedSchema = moduleExports.find(([key]) => key === schemaName)?.[1]
        ?? moduleExports.find(([key]) => key === 'default')?.[1];

      if (isObjectSchemaLike(loadedSchema)) {
        return loadedSchema;
      }
      
      console.error(`❌ Schema for ${resolvedFormKey} is not a valid ZodObject.`);
      return null;
    } catch (error) {
      console.error(`❌ Error loading schema for ${formKey}:`, error);
      return null;
    }
  }


  static async getRequiredVariablesForForm(formKey: string, availableForms?: string[]): Promise<string[]> {
    try {
      const schema = await this.loadFormSchema(formKey, availableForms);
      if (schema) {
        const names = this.extractVariableNamesFromSchema(schema);
        return getFormKeyName(formKey) === 'alta-propuesta'
          ? names.filter((name) => name !== 'closed_won_client_data' && name !== 'closed_won_recovery_data')
          : names;
      }
      return [];
    } catch (error) {
      console.warn(`Error getting required variables for form ${formKey}:`, error);
      return [];
    }
  }

  private static getMimeTypeFromFilename(filename: string): string | null {
    const extension = filename.toLowerCase().split('.').pop();
    
    const mimeTypeByExtension = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'txt': 'text/plain',
      'zip': 'application/zip'
    };
    
    const mimeType = extension
      ? Object.entries(mimeTypeByExtension).find(([key]) => key === extension)?.[1]
      : undefined;
    return mimeType ?? null;
  }

  private static async readTaskFormVariableValue(
    taskId: string,
    [variableName, variable]: [string, TaskFormVariable],
  ): Promise<{ variableName: string; formValue: CamundaFormValue }> {
    let formValue: CamundaFormValue = null;

    if (variable.type === 'File') {
      const filename = variable.valueInfo?.filename;
      if (filename) {
        console.log(`4. Variable de tipo File encontrada: '${variableName}'. Descargando contenido...`);
        const fileContent = await this.getTaskVariableBinary(taskId, variableName);
        if (fileContent) {
          const mimeType = this.getMimeTypeFromFilename(filename)
            || fileContent.type
            || 'application/octet-stream';
          formValue = new File([fileContent], filename, { type: mimeType });
          console.log(`5. Variable '${variableName}' reemplazada por el objeto File.`, formValue);
        }
      }
    } else if (variable.type === 'Json' &&
      (variableName === 'closed_won_recovery_data' || variableName === 'closed_won_client_data')) {
      const rawValue = variable.value;
      const serialized = stringSchema.safeParse(rawValue);
      const parsed = serialized.success ? JSON.parse(serialized.data) : rawValue;
      const result = camundaFormValueSchema.safeParse(parsed);
      if (!result.success) throw new Error(`No se puede recuperar ${variableName} de Camunda`);
      formValue = result.data;
    } else if (variable.type === 'Object') {
      try {
        const rawValue = variable.value;
        const stringValue = stringSchema.safeParse(rawValue);
        const parsed = stringValue.success ? JSON.parse(stringValue.data) : rawValue;

        if (this.isSerializedMultiFileArray(parsed)) {
          formValue = this.reconstituteMultiFileItems(parsed);
          console.log(`Variable '${variableName}' reconstituida como MultiFileItem[]:`, formValue);
        } else {
          const parsedFormValue = camundaFormValueSchema.safeParse(parsed);
          formValue = parsedFormValue.success ? parsedFormValue.data : null;
          console.log(`Variable '${variableName}' deserializada de JSON:`, formValue);
        }
      } catch (error) {
        console.warn(`Error al parsear JSON para variable '${variableName}':`, error);
        const fallbackValue = camundaFormValueSchema.safeParse(variable.value);
        formValue = fallbackValue.success ? fallbackValue.data : null;
      }
    } else {
      const parsedFormValue = camundaFormValueSchema.safeParse(variable.value);
      formValue = parsedFormValue.success ? parsedFormValue.data : null;
    }

    return { variableName, formValue };
  }

  static async getTaskFormVariables(
    taskId: string,
    variableNames?: string[],
    deserializeValues = true,
  ): Promise<CamundaFormValues> {
    try {
      console.log('1. Obteniendo variables del formulario para tarea:', taskId);

      let url = `/task/${taskId}/form-variables`;
      if (variableNames && variableNames.length > 0) {
        const variableNamesParam = variableNames.join(',');
        url += `?variableNames=${encodeURIComponent(variableNamesParam)}`;
        if (variableNames.includes('closed_won_recovery_data')) url += '&deserializeValues=false';
        console.log('2. Solicitando solo variables específicas:', variableNames);
      } else {
        console.log('2. Solicitando todas las variables (sin filtro)');
      }

      if (!deserializeValues) url += `${url.includes('?') ? '&' : '?'}deserializeValues=false`;

      const variables = await this.makeRequest<TaskFormVariables>(url);
      debugCamundaForms('3. Variables recibidas de la API:', summarizeForLog(variables));

      const entries = Object.entries(variables);
      const formValueEntries: Array<{ variableName: string; formValue: CamundaFormValue }> = [];
      formValueEntries.length = entries.length;
      let nextEntryIndex = 0;
      const readNextEntry = async (): Promise<void> => {
        const entryIndex = nextEntryIndex;
        nextEntryIndex += 1;
        const entry = entries[entryIndex];
        if (!entry) {
          return;
        }
        formValueEntries[entryIndex] = await this.readTaskFormVariableValue(taskId, entry);
        return readNextEntry();
      };

      const workerCount = Math.min(TASK_FORM_BINARY_CONCURRENCY, entries.length);
      await Promise.all(Array.from({ length: workerCount }, () => readNextEntry()));

      const formValues: CamundaFormValues = {};
      for (const entry of formValueEntries) {
        formValues[entry.variableName] = entry.formValue;
      }

      debugCamundaForms('6. Variables finales devueltas por el servicio:', summarizeForLog(formValues));
      return formValues;
    } catch (error) {
      console.error('Error fetching task form variables:', error);
      throw error;
    }
  }

  static async getOptimizedTaskFormVariables(
    taskId: string,
    formKey: string,
    availableForms?: string[],
  ): Promise<CamundaFormValues> {
    console.log(`🚀 Obteniendo variables optimizadas para tarea ${taskId} con formulario ${formKey}`);
    
    const requiredVariables = await this.getRequiredVariablesForForm(formKey, availableForms);
    if (getFormKeyName(formKey) === 'revision-gestion-cuentas') {
      if (!requiredVariables.length) throw new Error('La revisión de cuentas no declara variables recuperables');
      return this.getTaskFormVariables(taskId, requiredVariables, false);
    }
    const isRpaRecoveryForm = formKey
      .replaceAll('\\', '/')
      .split('/')
      .includes('revision-rpa-cliente');

    if (isRpaRecoveryForm) {
      if (requiredVariables.length === 0) {
        throw new Error(`El formulario ${formKey} no declara variables recuperables`);
      }
      const recoveryVariables = Array.from(new Set([
        ...requiredVariables,
        LEGACY_RPA_RECOVERY_CCC_VARIABLE,
      ]));
      console.log(`🔒 Cargando variables declaradas y compatibilidad CCC controlada para ${formKey}`);
      const variables = await this.getTaskFormVariables(taskId, recoveryVariables);
      return recoverClosedWonFormValues(migrateLegacyRpaRecoveryAccount(variables));
    }

    console.log(`📋 Cargando TODAS las variables para evitar problemas de variables null`);
    const allVariables = await this.getTaskFormVariables(taskId);

    if (requiredVariables.length > 0) {
      console.log(`📝 Variables requeridas por el formulario ${formKey}:`, requiredVariables);
      console.log(`✅ Variables disponibles en Camunda:`, Object.keys(allVariables));
    }
    
    return omitClosedWonSnapshot(formKey, migrateLegacyRiskCompanyVariables(formKey, allVariables));
  }
}
